import { AssetTag } from "../../swagger/caasService-v1";
import { LEGEND_ACTIVITY_ORDER, colorForActivity, compareLegendOrder, compareTableOrder, normalizeActivityKey } from "./EnergyProductivity.activities";
/** Per-metric display unit shown in legends / in-bar text. */
const METRIC_UNITS = {
    CycleTime: "",
    EnergyUsed: "kWh",
    IdleTime: "%",
    LoadCount: "cycles",
    MaterialMoved: "tonnes"
};
/** Maps the segmented asset filter to the API `assetTag` (or `null` for All). */
const assetFilterToTag = (assetFilter) => {
    if (assetFilter === "Haulers")
        return AssetTag.HAULER;
    if (assetFilter === "Loaders")
        return AssetTag.LOADER;
    return null;
};
/**
 * Case-insensitive asset-tag match. The backend returns tags in upper case
 * (e.g. `"LOADER"`, `"HAULER"`) while the generated `AssetTag` enum is title
 * case (`"Loader"`, `"Hauler"`), so a strict `===` comparison would drop every
 * row under the Haulers / Loaders filters. `null` tag = "All" (matches every row).
 */
const matchesAssetTag = (rowTag, tag) => {
    if (tag === null)
        return true;
    return (rowTag ?? "").toUpperCase() === tag.toUpperCase();
};
/**
 * Normalises a raw asset tag to title case for display. The backend returns
 * upper-case tags (`"LOADER"`); the UI shows them as `"Loader"`.
 */
const toTitleCase = (value) => value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
/** Reads the numeric value for the selected bar metric from a productivity detail. */
const readMetricValue = (detail, metric) => {
    switch (metric) {
        case "EnergyUsed":
            return detail.energyUsed ?? 0;
        case "IdleTime":
            return detail.idleTime ?? 0;
        case "LoadCount":
            return detail.loadCount ?? 0;
        case "MaterialMoved":
            return detail.materialMoved ?? 0;
        default:
            return 0;
    }
};
/**
 * Builds human-readable, unique per-asset tick labels. When the same `assetTag`
 * appears more than once (e.g. two Loaders) the labels are numbered so each bar
 * is distinguishable; unique tags are shown as-is. Falls back to a shortened
 * `assetId`, then a positional label, when a tag is missing.
 */
const buildAssetLabels = (details) => {
    const tagTotals = new Map();
    details.forEach((detail) => {
        if (detail.assetTag) {
            const key = detail.assetTag.toUpperCase();
            tagTotals.set(key, (tagTotals.get(key) ?? 0) + 1);
        }
    });
    const tagSeen = new Map();
    return details.map((detail, index) => {
        if (detail.assetTag) {
            const key = detail.assetTag.toUpperCase();
            const label = toTitleCase(detail.assetTag);
            if ((tagTotals.get(key) ?? 0) > 1) {
                const occurrence = (tagSeen.get(key) ?? 0) + 1;
                tagSeen.set(key, occurrence);
                return `${label} ${occurrence}`;
            }
            return label;
        }
        if (detail.assetId)
            return detail.assetId.slice(0, 8);
        return `Asset ${index + 1}`;
    });
};
const buildBarSeries = (response, metric, assetFilter, dateKey) => {
    const tag = assetFilterToTag(assetFilter);
    const details = (response.productivityDetails ?? []).filter((detail) => matchesAssetTag(detail.assetTag, tag));
    return {
        assetFilter,
        data: details.map((detail) => readMetricValue(detail, metric)),
        dateKey,
        metric,
        tickLabels: buildAssetLabels(details),
        type: "bar",
        unit: METRIC_UNITS[metric]
    };
};
/** Rounds to at most two decimals for display. */
const roundDuration = (value) => Math.round(value * 100) / 100;
const buildDonutSeries = (response, assetFilter, dateKey) => {
    const tag = assetFilterToTag(assetFilter);
    const activities = (response.activities ?? []).filter((activity) => matchesAssetTag(activity.assetTag, tag));
    // Per backend/design direction the raw activity values are used directly as
    // the labels (no consolidation into fixed categories). Every known activity
    // is seeded at zero first so the donut, legend and table always show the
    // full design set even when the API returns no data (or omits some
    // activities). Durations are summed per activity, keyed by the normalised
    // name so en-dash / hyphen variants of "Key On – Engine Off" collapse into
    // a single entry and the seeded display label (with its design casing) wins.
    const totals = new Map();
    LEGEND_ACTIVITY_ORDER.forEach((label) => {
        totals.set(normalizeActivityKey(label), { duration: 0, label });
    });
    let unit = "";
    activities.forEach((activity) => {
        // Missing/blank activity labels are bucketed under "Unknown" so their
        // durations are still represented in the donut/table totals rather than
        // being silently dropped.
        const rawActivity = (activity.activity ?? "").trim() || "Unknown";
        const key = normalizeActivityKey(rawActivity);
        const existing = totals.get(key);
        totals.set(key, {
            duration: (existing?.duration ?? 0) + (activity.duration ?? 0),
            label: existing?.label ?? rawActivity
        });
        if (!unit && activity.unitOfMeasure)
            unit = activity.unitOfMeasure;
    });
    // Cycle Time durations are always in hours; fall back to that when the API
    // returned no rows to source a unit from, so the seeded zero rows still
    // read "0 hours".
    if (!unit)
        unit = "hours";
    // One slice + one table row per activity. The donut/legend follow the fixed
    // design order; the Activities table follows its own fixed design order.
    // Both are derived from the same per-activity totals.
    const entries = [...totals.values()];
    const slices = entries
        .slice()
        .sort((a, b) => compareLegendOrder(a.label, b.label))
        .map((entry) => ({
        color: colorForActivity(entry.label),
        label: entry.label,
        value: roundDuration(entry.duration)
    }));
    const activityRows = entries
        .slice()
        .sort((a, b) => compareTableOrder(a.label, b.label))
        .map((entry) => {
        const duration = roundDuration(entry.duration);
        return {
            label: entry.label,
            timeLabel: unit ? `${duration} ${unit}` : `${duration}`
        };
    });
    return {
        activities: activityRows,
        assetFilter,
        dateKey,
        metric: "CycleTime",
        slices,
        type: "donut",
        unit
    };
};
/**
 * Maps a `ProjectEnergyHistogramResponse` (aggregation = productivity) into the
 * chart series consumed by the Energy &amp; Productivity panel. Cycle Time
 * resolves to a donut series built from `activities`; every other metric
 * resolves to a bar series built from `productivityDetails`.
 */
export const mapProductivityResponseToSeries = ({ assetFilter, dateKey, metric, response }) => {
    if (metric === "CycleTime") {
        return buildDonutSeries(response, assetFilter, dateKey);
    }
    return buildBarSeries(response, metric, assetFilter, dateKey);
};
